import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  CardWrapper, ButtonUI, ButtonOutlined,
} from '../../UI';
import IdentificacaoCliente from '../Cliente/IdentificacaoCliente';
import EnderecoCliente from '../Cliente/EnderecoCliente';
import TelefoneCliente from '../Cliente/TelefoneCliente';
import EmailCliente from '../Cliente/EmailCliente';
import DadosAdvogadoCliente from '../Cliente/DadosAdvogadoCliente';
import DialogDocumentosCliente from '../Cliente/DialogDocumentosCliente';
import { mapearClienteParaFormFields } from '../Cliente/clienteController';
import usePodeConsultarOuEditarCliente from '../../../hooks/usePodeConsultarOuEditarCliente';
import usePodeEditarCliente from '../../../hooks/usePodeEditarCliente';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Cliente — print `grid_check_cliente.png`.
 *
 * Mesmo esqueleto da aba Devedor, com o bloco "Identificação" e o avatar no
 * canto direito do cabeçalho.
 *
 * AW2-1136 Etapa 0: `dadosCliente` (dado real, `buscarCadastroCliente` via
 * `ControleProcessosPage.jsx`) chega como prop nova.
 *
 * AW2-1136 Etapa 1: `formFields` (`clienteController.mapearClienteParaFormFields`)
 * substitui o bloco "Identificação" mockado por `IdentificacaoCliente.jsx`
 * novo, com dado real. Diferente da aba Devedor, `formFields` aqui é um
 * valor DERIVADO simples (`const`, não `useState`) — a aba Cliente real não
 * tem modo de edição in-place (confirmado por leitura direta de
 * `DadosCliente.jsx` real, todo campo sempre `disabled`), então não existe
 * rascunho local para manter em sincronia nem `useEffect` de reset ao trocar
 * de processo — `dadosCliente` já é a única fonte de verdade, sem
 * intermediário editável.
 *
 * AW2-1136 Etapa 2: `EnderecoCliente.jsx` (endereço único, sem lista) e
 * `TelefoneCliente.jsx` (lista somente-leitura, sem ícone de
 * copiar/excluir) substituem `BlocoEnderecos`/`BlocoTelefones` mockados.
 *
 * AW2-1136 Etapa 3: `EmailCliente.jsx` (lista somente-leitura, "Padrão" SEM
 * exclusividade mútua — AC6) e `DadosAdvogadoCliente.jsx` (telefone como
 * sub-objeto, mais rico que o de Devedor) novos, sem mock anterior.
 *
 * AW2-1136 Etapa 4: avatar removido (produção real não tem nenhum ícone de
 * avatar nesta tela — confirmado por leitura direta de `DadosCliente.jsx`
 * real, o `AccountCircleOutlinedIcon`/tooltip "Preposto do cliente" era só
 * do mock). "CONSULTAR CLIENTE" (renomeado de "Inspecionar Cliente", ícone
 * removido) e "ALTERAR" (botão novo, não existe em produção ainda) navegam
 * de verdade para o cadastro completo do cliente (`window.open`, nova aba)
 * — gate de permissão diferente entre os dois: Consultar aceita 501 OU 504
 * (mesma condição real), Alterar exige só 504 (AC4). Rodapé
 * "CANCELAR"/"GRAVAR" removido — mock antigo sem função real (a aba não tem
 * modo de edição in-place, toda edição acontece nas telas para onde os 2
 * botões acima navegam).
 *
 * AW2-1136 Etapa 5: "VISUALIZAR DOCUMENTO" abre `DialogDocumentosCliente.jsx`
 * novo (AC1) — popup MUI v5 com a mesma tabela paginada real de
 * `DocumentosCliente.jsx` (modo `isDisplayMode`), sem nenhum controle de
 * edição.
 */
export default function AbaCliente({ dadosCliente, podeModificarFase }) {
  const formFields = mapearClienteParaFormFields(dadosCliente);
  const podeConsultarOuEditar = usePodeConsultarOuEditarCliente();
  const podeEditar = usePodeEditarCliente();
  const [documentosAberto, setDocumentosAberto] = useState(false);

  const onClickConsultarCliente = () => {
    window.open(`/cliente/${dadosCliente?.idCliente}/detalhar`, '_blank');
  };

  const onClickAlterar = () => {
    window.open(`/cliente/${dadosCliente?.idCliente}/editar`, '_blank');
  };

  return (
    <CardWrapper>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap',
      }}
      >
        <PersonOutlineIcon sx={{ color: C.navy }} />
        <Typography variant="h2" sx={{ flex: 1 }}>Cliente</Typography>
        <ButtonOutlined
          size="small"
          onClick={() => setDocumentosAberto(true)}
          disabled={!dadosCliente?.idCliente}
        >
          VISUALIZAR DOCUMENTO
        </ButtonOutlined>
        <ButtonOutlined
          size="small"
          onClick={onClickConsultarCliente}
          disabled={!(podeModificarFase && podeConsultarOuEditar)}
        >
          CONSULTAR CLIENTE
        </ButtonOutlined>
        <ButtonUI
          size="small"
          onClick={onClickAlterar}
          disabled={!(podeModificarFase && podeEditar)}
        >
          ALTERAR
        </ButtonUI>
      </Box>

      <Typography variant="h3" sx={{ mb: 1.5 }}>Identificação</Typography>

      <IdentificacaoCliente formFields={formFields} />

      <EnderecoCliente endereco={formFields.endereco} />
      <TelefoneCliente telefones={formFields.telefones} />
      <EmailCliente emails={formFields.emails} />
      <DadosAdvogadoCliente
        nomeAdvogado={formFields.nomeAdvogado}
        telefoneAdvogado={formFields.telefoneAdvogado}
      />

      <DialogDocumentosCliente
        open={documentosAberto}
        idCliente={dadosCliente?.idCliente}
        onClose={() => setDocumentosAberto(false)}
      />
    </CardWrapper>
  );
}
AbaCliente.propTypes = {
  dadosCliente: PropTypes.object,
  podeModificarFase: PropTypes.bool,
};
AbaCliente.defaultProps = {
  dadosCliente: null,
  podeModificarFase: false,
};
